import React from "react";
import { Navigate, generatePath, useLocation, useParams } from "react-router-dom";
import { authLayout } from "../helpers/withAuth";

// The per-category trailer pages are now one page with filters. Old links and
// bookmarks land on the matching slice of it.
const TRAILER_REDIRECTS = {
  "/youtube/alltrailers": "",
  "/youtube/movietrailers": "?categories=Movies",
  "/youtube/seriestrailers": "?categories=Series",
  "/youtube/gamestrailers": "?categories=Games",
  "/youtube/animetrailers": "?categories=Anime",
  "/youtube/kidstrailers": "?audience=Made+for+kids",
  "/youtube/unofficialtrailers": "",
};

const trailerRedirectRoutes = [
  ...Object.entries(TRAILER_REDIRECTS).map(([path, query]) => ({
    path,
    element: <Navigate to={`/youtube/trailers/list${query}`} replace />,
  })),
  {
    path: "/youtube/unmappedtrailers",
    element: <Navigate to="/youtube/trailers/unmapped" replace />,
  },
];

// The discovery pages were renamed. Old links keep their filters and tab,
// which live in the query string, and their ids, which are filled into `to`.
const RedirectKeepingQuery = ({ to }) => {
  const { search } = useLocation();
  const params = useParams();
  return <Navigate to={`${generatePath(to, params)}${search}`} replace />;
};

const discoveryRedirectRoutes = [
  { path: "/youtube/discovery/candidates", to: "/youtube/discovery/creators" },
  { path: "/youtube/discovery/series", to: "/youtube/discovery/content" },
  { path: "/youtube/discovery/channels/:channelId", to: "/youtube/discovery/creators/:channelId" },
  {
    path: "/youtube/discovery/channels/:channelId/series/:slug",
    to: "/youtube/discovery/creators/:channelId/content/:slug",
  },
].map(({ path, to }) => ({ path, element: <RedirectKeepingQuery to={to} /> }));

export const youtubeRoutes = {
  path: "youtube",
  ...authLayout("YouTube", [
    {
      path: "/youtube/",
      lazy: () =>
        import("../../modules/youtube/home").then((m) => ({
          Component: m.YoutubeHomePage,
        })),
    },
    {
      path: "/youtube/trends",
      lazy: () =>
        import("../../modules/youtube/youtube-trends").then((m) => ({
          Component: m.default,
        })),
    },
    {
      path: "/youtube/channels",
      lazy: () =>
        import("../../modules/youtube/channelsList").then((m) => ({
          Component: m.YoutubeChannels,
        })),
    },
    {
      path: "/youtube/discovery",
      lazy: () =>
        import("../../modules/youtube/creatorDiscovery/overview").then((m) => ({
          Component: m.CreatorDiscoveryOverviewPage,
        })),
    },
    {
      path: "/youtube/discovery/creators",
      lazy: () =>
        import("../../modules/youtube/creatorDiscovery/candidates").then((m) => ({
          Component: m.CreatorCandidatesPage,
        })),
    },
    {
      path: "/youtube/discovery/content",
      lazy: () =>
        import("../../modules/youtube/creatorDiscovery/series").then((m) => ({
          Component: m.CreatorSeriesPage,
        })),
    },
    {
      path: "/youtube/discovery/creators/:channelId",
      lazy: () =>
        import("../../modules/youtube/creatorDiscovery/channel").then((m) => ({
          Component: m.CreatorChannelPage,
        })),
    },
    {
      path: "/youtube/discovery/creators/:channelId/content/:slug",
      lazy: () =>
        import("../../modules/youtube/creatorDiscovery/seriesDetail").then((m) => ({
          Component: m.CreatorSeriesWorkPage,
        })),
    },
    {
      path: "/youtube/trailers",
      lazy: () =>
        import("../../modules/youtube/trailersOverview").then((m) => ({
          Component: m.YoutubeTrailersOverviewPage,
        })),
    },
    {
      path: "/youtube/trailers/list",
      lazy: () =>
        import("../../modules/youtube/trailers").then((m) => ({
          Component: m.YoutubeTrailers,
        })),
    },
    {
      path: "/youtube/trailers/unmapped",
      lazy: () =>
        import("../../modules/youtube/trailers").then((m) => ({
          Component: m.YoutubeUnmappedTrailers,
        })),
    },
    {
      path: "/youtube/channels/:id",
      lazy: () =>
        import("../../modules/youtube/channelPage").then((m) => ({
          Component: m.default,
        })),
    },
    {
      path: "/youtube/videos/:id",
      lazy: () =>
        import("../../modules/youtube/videoPages").then((m) => ({
          Component: m.default,
        })),
    },
    ...trailerRedirectRoutes,
    ...discoveryRedirectRoutes,
  ]),
};
